/**
 * A page loading (Stage 4, Sam 2026-10-20: "a quick, easy, hey, this page is loading"): the launch's petals, centred in
 * the page. A few petals at a time travel round from 12 o'clock; once the page is ready the circle fills to the top and the
 * centre dot lands, then it fades and the page shows.
 *
 * usePageLoading(loading) gives a page that finish: while loading (and for the dot after) it says show the loader, and
 * a load quicker than SHOW_AFTER_MS never shows one at all.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import PetalMark from './PetalMark';
import { motionFlags } from '../../lib/motionFlags';

// Sam, 2026-10-20: never a full circle until the page is ready - a few petals at a time go round, and only when it is
// ready does the circle fill to 12 o'clock and the dot land. (PetalMark comet variants.)
export const LOADERS = {
  comet3: {
    label: 'Three going round',
    note: 'Three petals at a time; ready, it fills to the top and the dot lands.',
  },
  comet4: { label: 'Four going round', note: 'Four petals at a time, a little quicker.' },
  cometGrow: {
    label: 'Growing round',
    note: 'Three petals, one more each lap while it is still loading.',
  },
};
const SHOW_AFTER_MS = 350; // a load quicker than this never shows the loader (Sam, 2026-10-20)
const SETTLE_MS = 150;

export default function PetalLoader({ variant, ready = false, size = 72, onDone, label = null }) {
  const [out, setOut] = useState(false);
  const v = LOADERS[variant]
    ? variant
    : LOADERS[motionFlags().loader]
      ? motionFlags().loader
      : 'comet3';
  const onFinished = () => {
    setOut(true);
    setTimeout(() => onDone?.(), 260);
  };
  useEffect(() => {
    if (!ready) setOut(false);
  }, [ready]);
  return (
    <div
      className={`petal-loader${out ? ' petal-loader--out' : ''}`}
      role="status"
      aria-label={label ?? 'Loading'}
    >
      <PetalMark variant={v} size={size} ready={ready} glow={false} onFinished={onFinished} />
      {label && <span className="petal-loader__label">{label}</span>}
    </div>
  );
}

/** { show, node }: render `node` in place of the page while `show`; it is the loader, or an empty frame for the first moment. */
export function usePageLoading(loading, { latch = true } = {}) {
  // latch: false until the page knows who you are
  // Off in the live app (Sam, 2026-10-20: "get rid of the page loader... keep it in the code base"): MOTION_DEFAULTS.loader
  // is 'off', so a page with nothing to show yet stays blank, and the petals are only in /lab/motion (?loader=).
  const enabled = motionFlags().loader !== 'off';
  // Once the page has shown its content it never goes back to loading on this visit - a flag flipping later (the session
  // arriving, a refetch) brought the loader back over a page already on screen.
  const shownRef = useRef(false);
  const [visible, setVisible] = useState(false); // the loader is on screen (the load outlasted SHOW_AFTER_MS)
  const [finishing, setFinishing] = useState(false); // loaded while visible: the lap completes and the dot lands
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  // Loading flips off for a moment between one step and the next (the saved cache restoring, then the fetch): the end
  // counts only once it has held for SETTLE_MS, or the loader finished and started again from the top (Sam, 2026-10-20).
  const [settled, setSettled] = useState(!loading);
  useEffect(() => {
    if (loading) {
      setSettled(false);
      return undefined;
    }
    const t = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(t);
  }, [loading]);
  const busy = loading || !settled;
  useEffect(() => {
    if (busy) {
      setFinishing(false);
      const t = setTimeout(() => setVisible(true), SHOW_AFTER_MS);
      return () => clearTimeout(t);
    }
    if (visibleRef.current) setFinishing(true);
    else setVisible(false);
    return undefined;
  }, [busy]);
  const done = useCallback(() => {
    setVisible(false);
    setFinishing(false);
  }, []);
  // Not yet visible and already done: the page shows straight away (cached data, a quick load) - no loader, no wait.
  let show = enabled ? (busy && (visible || loading)) || finishing : loading;
  if (shownRef.current) show = false;
  else if (!show && latch) shownRef.current = true;
  const node =
    !enabled || !visible ? (
      <div className="petal-loader" aria-busy="true" />
    ) : (
      <PetalLoader ready={!busy} onDone={done} />
    );
  return { show, node };
}
